// undoSteps — what an Undo or Redo sends, pure.
//
// Undo and Redo are saved (the real-time editor plan, PR2): a step is sent
// through the save queue as the same commands an edit makes, so a reload
// keeps it. A history step holds the tree before and after its edit; undoing
// it sends what takes the blocks it touched from the after-tree back to the
// before-tree (redo: the other way round):
//   - a text goes back as a text save built on the text there now (its
//     `baseText`, so the server refuses it if that block changed elsewhere);
//   - an insert as a remove, a remove as the same block appended and moved
//     back into place, a move as a move back;
//   - a retype as a retype, attrs (level, answer mode, correct, feedback) as
//     a merge patch of the old values.
//
// Element-level, like the text saves (PR3): only the blocks the step touched
// are compared against what is shown now. A change elsewhere to another block
// leaves the step undoable; one to a block the step touched refuses it here,
// and the server refuses what this client has not seen yet.

export type { HistoryStep, UndoOp };
export { correctOp, HISTORY_LIMIT, planStep };

import { exhaustSwitchCase } from "@bilda/utils";
import type { Choice, Element, ElementId } from "../Editor/types.js";
import { primaryTextOf } from "./edits.js";
import type { Snapshot } from "./types.js";

// The most steps one material's history keeps (the oldest go first).
const HISTORY_LIMIT = 100;

// One edit in the history: the editor before it and after it.
type HistoryStep = {
    readonly before: Snapshot;
    readonly after: Snapshot;
};

type Position = "before" | "after";

// One command an Undo or Redo sends, in order.
type UndoOp =
    | { readonly type: "remove"; readonly id: ElementId }
    | { readonly type: "append-block"; readonly block: Element }
    | {
          readonly type: "append-choice";
          readonly mcqId: ElementId;
          readonly choice: Choice;
      }
    | {
          readonly type: "move";
          readonly id: ElementId;
          readonly anchor: ElementId;
          readonly position: Position;
      }
    | {
          readonly type: "text";
          readonly id: ElementId;
          readonly text: string;
          readonly base: string;
      }
    | {
          readonly type: "attrs";
          readonly id: ElementId;
          readonly attrs: Readonly<Record<string, unknown>>;
      }
    | { readonly type: "retype"; readonly id: ElementId; readonly to: Element };

type StepPlan =
    // A block the step touched changed elsewhere since: nothing is sent.
    | { readonly type: "changed-elsewhere" }
    | {
          readonly type: "plan";
          readonly ops: readonly UndoOp[];
          // What the editor shows once the step is taken.
          readonly shown: readonly Element[];
          // The element the step adds back (its path is threaded in).
          readonly mints: ElementId | undefined;
      };

type Ordered = { readonly id: ElementId };

// `items` with `id` moved to just before or after `anchor`.
const placeAt = <T extends Ordered>(
    items: readonly T[],
    item: T,
    anchor: ElementId,
    position: Position,
): readonly T[] => {
    const without = items.filter((x) => x.id !== item.id);
    const at = without.findIndex((x) => x.id === anchor);
    const cut = position === "after" ? at + 1 : at;
    return [...without.slice(0, cut), item, ...without.slice(cut)];
};

// The ops that turn the order of `from` into the order of `to`: removes,
// then each item of `to` in turn appended (when new, by `append`) and moved
// right after the one `to` has before it, when it is not there already. A
// single move is one op. An appended item is always moved into place: it
// lands at the end of what the server has, which may hold more by then.
const orderOps = <T extends Ordered>(
    from: readonly T[],
    to: readonly T[],
    append: (item: T) => UndoOp,
): readonly UndoOp[] => {
    const wanted = new Set(to.map((x) => x.id));
    const removes = from
        .filter((x) => !wanted.has(x.id))
        .map((x): UndoOp => ({ type: "remove", id: x.id }));
    const placed = to.reduce<{
        readonly ids: readonly Ordered[];
        readonly ops: readonly UndoOp[];
    }>(
        (acc, item, i) => {
            const isNew = !acc.ids.some((x) => x.id === item.id);
            const there = isNew
                ? { ids: [...acc.ids, item], ops: [...acc.ops, append(item)] }
                : acc;
            const want = to[i - 1]?.id;
            const at = there.ids.findIndex((x) => x.id === item.id);
            const have = there.ids[at - 1]?.id;
            const first = there.ids.find((x) => x.id !== item.id)?.id;
            if ((have === want && !isNew) || first === undefined) return there;
            const anchor = want ?? first;
            const position: Position = want === undefined ? "before" : "after";
            return {
                ids: placeAt(there.ids, item, anchor, position),
                ops: [
                    ...there.ops,
                    { type: "move", id: item.id, anchor, position },
                ],
            };
        },
        { ids: from.filter((x) => wanted.has(x.id)), ops: [] },
    );
    return [...removes, ...placed.ops];
};

const textOps = (id: ElementId, from: string, to: string): UndoOp[] =>
    from === to ? [] : [{ type: "text", id, text: to, base: from }];

// A merge patch of `attrs` when `differs` (else nothing).
const attrsOps = (
    differs: boolean,
    id: ElementId,
    attrs: Readonly<Record<string, unknown>>,
): UndoOp[] => (differs ? [{ type: "attrs", id, attrs }] : []);

const choiceOps = (from: Choice, to: Choice): readonly UndoOp[] => [
    ...textOps(to.id, from.text, to.text),
    ...attrsOps(from.correct !== to.correct, to.id, { correct: to.correct }),
    ...attrsOps((from.feedback ?? "") !== (to.feedback ?? ""), to.id, {
        feedback: to.feedback ?? "",
    }),
];

// The ops that take one block from `from` to `to` (the same id).
const blockOps = (from: Element, to: Element): readonly UndoOp[] => {
    if (from.type !== to.type) return [{ type: "retype", id: to.id, to }];
    const text = textOps(to.id, primaryTextOf(from), primaryTextOf(to));
    switch (to.type) {
        case "heading":
            return [
                ...text,
                ...attrsOps(
                    from.type === "heading" && from.level !== to.level,
                    to.id,
                    { level: to.level },
                ),
            ];
        case "prose":
        case "objective":
        case "note":
            return text;
        case "mcq":
            return from.type !== "mcq"
                ? text
                : [
                      ...text,
                      ...attrsOps(from.answerMode !== to.answerMode, to.id, {
                          answerMode: to.answerMode,
                      }),
                      ...orderOps(
                          from.choices,
                          to.choices,
                          (choice): UndoOp => ({
                              type: "append-choice",
                              mcqId: to.id,
                              choice,
                          }),
                      ),
                      ...to.choices.flatMap((c) => {
                          const was = from.choices.find((x) => x.id === c.id);
                          return was === undefined ? [] : choiceOps(was, c);
                      }),
                  ];
        // A part is never changed here (its own page is).
        case "part":
            return [];
        default:
            return exhaustSwitchCase(to);
    }
};

// The element an op is about.
const targetOf = (op: UndoOp): ElementId => {
    switch (op.type) {
        case "append-block":
            return op.block.id;
        case "append-choice":
            return op.choice.id;
        case "remove":
        case "move":
        case "text":
        case "attrs":
        case "retype":
            return op.id;
        default:
            return exhaustSwitchCase(op);
    }
};

const find = (tree: readonly Element[], id: ElementId) =>
    tree.find((x) => x.id === id);
const before = (tree: readonly Element[], id: ElementId) =>
    tree[tree.findIndex((x) => x.id === id) - 1]?.id;

// The block order op applied to what is shown.
const applyTop = (tree: readonly Element[], op: UndoOp): readonly Element[] => {
    switch (op.type) {
        case "remove":
            return tree.filter((x) => x.id !== op.id);
        case "append-block":
            return [...tree, op.block];
        case "move": {
            const block = find(tree, op.id);
            return block === undefined
                ? tree
                : placeAt(tree, block, op.anchor, op.position);
        }
        case "append-choice":
        case "text":
        case "attrs":
        case "retype":
            return tree;
        default:
            return exhaustSwitchCase(op);
    }
};

// Take the step from `from` to `to` on what is shown `now`: refused when a
// block it touches (or a block it moves next to) is no longer as `from` left
// it, else the ops to send and the tree to show.
const planStep = (
    now: readonly Element[],
    from: readonly Element[],
    to: readonly Element[],
): StepPlan => {
    const top = orderOps(
        from,
        to,
        (block): UndoOp => ({ type: "append-block", block }),
    );
    const inner = to.flatMap((b) => {
        const was = find(from, b.id);
        return was === undefined ? [] : blockOps(was, b);
    });
    const moved = new Set(top.filter((op) => op.type === "move").map(targetOf));
    const touched = new Set([
        ...top.map(targetOf),
        ...to.filter((b) => changed(from, b)).map((b) => b.id),
    ]);
    const asLeft = [...touched].every((id) => {
        const shown = find(now, id);
        const left = find(from, id);
        if (shown === undefined || left === undefined) return shown === left;
        return (
            blockOps(left, shown).length === 0 &&
            (!moved.has(id) || before(now, id) === before(from, id))
        );
    });
    // Every block a move lands next to is there (or added back by the step).
    const anchored = top.every(
        (op) =>
            op.type !== "move" ||
            find(now, op.anchor) !== undefined ||
            find(from, op.anchor) === undefined,
    );
    if (!asLeft || !anchored) return { type: "changed-elsewhere" };
    const shown = top
        .reduce(applyTop, now)
        .map((b) => (touched.has(b.id) ? (find(to, b.id) ?? b) : b));
    const ops = [...top, ...inner];
    const mint = ops.find(
        (op) => op.type === "append-block" || op.type === "append-choice",
    );
    return {
        type: "plan",
        ops,
        shown,
        mints: mint === undefined ? undefined : targetOf(mint),
    };
};

// Whether block `b` of the target differs from its namesake in `from`.
const changed = (from: readonly Element[], b: Element): boolean => {
    const was = find(from, b.id);
    return was !== undefined && blockOps(was, b).length > 0;
};

// A choice's correct flag as the merge patch that saves it.
const correctOp = (a: {
    readonly choiceId: ElementId;
    readonly correct: boolean;
}): UndoOp => ({
    type: "attrs",
    id: a.choiceId,
    attrs: { correct: a.correct },
});
